import { authManager } from '../authManager';
import { buildApiUrl } from '../../utils/apiUrl';

/**
 * Service des cas d'usage intégrés.
 *
 * Il n'utilise pas BaseApiService : celle-ci impose un timeout de 15 secondes,
 * trop court pour une génération complète. On garde en revanche la même
 * construction d'URL et le même porteur de jeton.
 */

// Une génération de budget peut demander plus d'une minute.
const TIMEOUT_GENERATION = 180000;
const TIMEOUT_COURT = 15000;

export interface BudgetLine {
  designation: string;
  /** Code budgétaire officiel PEV (séries VH, T, R, L, H, F, ECF, MA, P). */
  code: string;
  unite: string;
  quantite: number;
  cout_unitaire: number;
  montant: number;
  /** Document d'où provient le coût unitaire, ou « À renseigner ». */
  source: string;
  commentaire: string;
}

export interface BudgetRubrique {
  nom: string;
  lignes: BudgetLine[];
  sous_total: number;
}

export interface Budget {
  titre: string;
  devise: string;
  /** Taux BCRG appliqué pour la contre-valeur en dollars. */
  taux_change_gnf_usd?: number;
  hypotheses: string[];
  rubriques: BudgetRubrique[];
  total: number;
}

export interface AssistantMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  payload: Budget | any | null;
  created_at: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
    cache_read_tokens: number;
    retries: number;
  };
}

/** Précision demandée avant rédaction, quand la demande manque d'assise. */
export interface QuestionCadrage {
  question: string;
  /** Ce que la réponse permet de produire. */
  pourquoi: string;
  exemple: string;
}

/** Passage justifiant une affirmation, confronté au document avant affichage. */
export interface Citation {
  /** Numéro de la source dans l'activité, tel que cité sous la forme [S1]. */
  source: number;
  extrait: string;
  /** Nom du document, inscrit au moment du contrôle. */
  document?: string;
}

/** Étapes successives d'une génération suivie au fil de l'eau. */
export type EvenementFlux =
  /** Travail en cours, quand la sortie ne peut pas encore être montrée. */
  | { type: 'etape'; libelle: string }
  /** Fragment de texte à ajouter à l'écran. */
  | { type: 'texte'; delta: string }
  /** Les contrôles ont échoué : ce qui est affiché va être remplacé. */
  | { type: 'reprise'; motif: string }
  | { type: 'fin'; message: AssistantMessage }
  | { type: 'erreur'; detail: string };

export interface ConversationDetail {
  id: number;
  title: string;
  template: number;
  template_title?: string;
  output_format:
    | 'texte' | 'document' | 'tableau' | 'presentation' | 'carnet' | 'audio'
    | 'image' | 'restitution' | 'capsule';
  /** Activité de rattachement, si la conversation en a une. */
  project?: number | null;
  project_name?: string | null;
  messages: AssistantMessage[];
}

/**
 * Appel authentifié, rejoué une fois si le token a expiré.
 *
 * Le renouvellement périodique laisse passer les tokens qui expirent pendant
 * qu'on ne regarde pas — machine en veille, onglet en arrière-plan. La requête
 * partait alors avec un token périmé et le refus du serveur (« Given token not
 * valid for any token type ») remontait tel quel à l'écran. On renouvelle au
 * moment du refus et on rejoue ; si le renouvellement échoue vraiment, c'est
 * que la session est finie et l'erreur redevient légitime.
 */
export async function appelAuthentifie(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const envoyer = () => fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${authManager.getAccessToken()}`,
    },
  });

  const reponse = await envoyer();
  if (reponse.status !== 401) return reponse;
  return (await authManager.renouvelerJeton()) ? envoyer() : reponse;
}

export async function requete<T>(endpoint: string, options: RequestInit = {}, timeout = TIMEOUT_COURT): Promise<T> {
  const controleur = new AbortController();
  const minuteur = setTimeout(() => controleur.abort(), timeout);

  try {
    const reponse = await appelAuthentifie(buildApiUrl(endpoint), {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      signal: controleur.signal,
    });

    if (!reponse.ok) {
      const corps = await reponse.json().catch(() => ({}));
      throw new Error(corps.detail || `Erreur ${reponse.status}`);
    }
    return (await reponse.json()) as T;
  } catch (erreur: any) {
    if (erreur.name === 'AbortError') {
      throw new Error("L'assistant met trop de temps à répondre. Réessayez.");
    }
    throw erreur;
  } finally {
    clearTimeout(minuteur);
  }
}

export interface SuggestionAssistant {
  id: number;
  titre: string;
  description: string;
  raison: string;
  pertinence: 'forte' | 'moyenne' | 'faible';
}

export interface ResumeConversation {
  id: number;
  title: string;
  template: number;
  template_title: string;
  updated_at: string;
}

export interface Projet {
  id: number;
  name: string;
  description?: string;
  nb_conversations: number;
  updated_at?: string;
}

export interface Voix {
  id: string;
  nom: string;
  langue: string;
}

/** Une réplique d'un contenu sonore : qui parle, quoi, et sur quel ton. */
export interface SegmentAudio {
  locuteur: string;
  texte: string;
  intention: string;
}

export interface ContenuSonore {
  titre: string;
  support: string;
  duree_cible_secondes: number;
  /** Durée de lecture mesurée sur le texte, ajoutée au contrôle. */
  duree_estimee_secondes?: number;
  segments: SegmentAudio[];
  mentions_obligatoires: string[];
}

/** Suivi d'usage et de dépense, réservé à l'administration. */
export interface Journal {
  synthese: {
    utilisateurs_actifs: number;
    comptes: number;
    generations: number;
    traces: number;
    jetons_entree: number;
    jetons_sortie: number;
    jetons_cache: number;
    cout_total: number;
    /** Part des données d'entrée servie par le cache, en pourcentage. */
    part_cache: number;
  };
  utilisateurs: Array<{
    id: number;
    nom: string;
    email: string;
    role: string;
    /** Service de rattachement ; la plateforme n'a pas de notion de licence. */
    service: string;
    poste: string;
    actif: boolean;
    derniere_connexion: string | null;
    inscrit_le: string;
    actions: number;
    generations: number;
    jetons: number;
    cout: number;
    derniere_activite: string | null;
  }>;
  assistants: Array<{
    id: number;
    titre: string;
    format: string;
    acces: number;
    generations: number;
    cout: number;
  }>;
  traces: Array<{
    id: number;
    utilisateur: string;
    action: string;
    assistant: string;
    date: string;
    ip: string;
  }>;
}

/** Annuaire des numéros rattachés et portée du canal WhatsApp. */
export interface CanalWhatsApp {
  numeros: Array<{
    id: number;
    numero: string;
    utilisateur: string;
    email: string;
    verifie: boolean;
    actif: boolean;
    en_attente_code: boolean;
    rattache_le: string;
    dernier_message: string | null;
  }>;
  assistants: Array<{ id: number; titre: string; format: string; ouvert: boolean }>;
  candidats: Array<{ id: number; nom: string; email: string }>;
  /** État de la session WhatsApp, tenu par la passerelle. */
  session: {
    disponible: boolean;
    detail?: string;
    appairee?: boolean;
    numero_emetteur?: string | null;
    etat?: string;
    qr_disponible?: boolean;
  };
}

/** Consigne visuelle rédigée par l'assistant, avant exécution. */
export interface ConsigneVisuelle {
  titre: string;
  support: string;
  format_image: string;
  intention: string;
  /** Consigne adressée au modèle d'images ; retouchable avant exécution. */
  invite: string;
  /** Message écrit, composé à côté de l'image et jamais dedans. */
  legende: string;
  mentions_obligatoires: string[];
}

/** Restitution vidéo : des diapositives commentées, montées en un film. */
export interface Restitution {
  titre: string;
  sous_titre: string;
  slides: { titre: string; puces: string[]; notes: string }[];
}

/** Capsule : un découpage de plans à tourner, chacun avec sa narration. */
export interface Capsule {
  titre: string;
  public: string;
  plans: { description: string; narration: string }[];
  mentions_obligatoires: string[];
}

/** Travail long suivi hors de la requête qui l'a demandé. */
export interface Tache {
  id: number;
  genre: string;
  etat: 'attente' | 'en_cours' | 'terminee' | 'echouee';
  avancement: string;
  erreur: string;
  resultat: { fichier?: string; titre?: string; octets?: number };
  duree_secondes: number;
}

/** Tour de parole situé dans l'enregistrement. */
export interface TourDeParole {
  locuteur: string;
  texte: string;
  debut: number;
  fin: number;
}

/**
 * Compte rendu structuré d'une réunion.
 *
 * Chaque élément porte l'extrait de transcription qui le fonde : c'est ce qui
 * permet de le vérifier avant de le valider.
 */
export interface CompteRendu {
  titre: string;
  resume: string;
  participants: string[];
  points: { sujet: string; teneur: string; extrait: string }[];
  decisions: { decision: string; sujet: string; extrait: string }[];
  actions: { action: string; responsable: string; echeance: string; extrait: string }[];
  /** Rubriques du plan imposé par le service, s'il y en a un. */
  sections?: { titre: string; contenu: string; extrait: string }[];
}

export type EtatReunion = 'deposee' | 'en_traitement' | 'a_relire' | 'validee' | 'echouee';

/** Réunion déposée, transcrite, relue puis versée à la mémoire de l'activité. */
export interface Reunion {
  id: number;
  titre: string;
  tenue_le: string | null;
  projet: number;
  projet_nom: string;
  etat: EtatReunion;
  moteur: string;
  erreur: string;
  deposee_le: string;
  validee_le: string | null;
  deposee_par: string;
  /** Le compte rendu a rejoint les documents de l'activité. */
  versee: boolean;
  compte_rendu: CompteRendu | Record<string, never>;
  /** Plan de compte rendu demandé au dépôt ; vide pour le plan standard. */
  plan_compte_rendu?: string;
  /**
   * L'enregistrement est encore là et peut être réécouté.
   *
   * Il ne l'est pas toujours : il est effacé au versement du compte rendu,
   * et le disque de l'hébergeur est reconstruit à chaque déploiement.
   */
  a_enregistrement?: boolean;
  /** Jointes à la relecture seulement. */
  transcription?: string;
  tours?: TourDeParole[];
}

export interface Personne {
  id: number;
  nom: string;
  email: string;
  /** Le créateur de l'activité ne peut pas en être retiré. */
  createur?: boolean;
}

export interface EquipeBreve {
  id: number;
  name: string;
}

export interface Membres {
  equipe: EquipeBreve | null;
  membres: Personne[];
}

export interface DocumentActivite {
  id: number;
  name: string;
  caracteres: number;
  uploaded_at: string;
}

export interface ProjetDetail extends Projet {
  conversations: Array<{
    id: number;
    title: string;
    template: number;
    template_title: string;
    auteur: string;
    updated_at: string;
  }>;
  productions: Array<{
    conversation: number;
    message: number;
    titre: string;
    format: string;
    created_at: string;
  }>;
}

/** Extension de fichier correspondant à un type MIME produit par l'API. */
const EXTENSIONS: Record<string, string> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/pdf': 'pdf',
  'audio/mpeg': 'mp3',
  'video/mp4': 'mp4',
  'image/png': 'png',
};

/**
 * Extension de repli, déduite du type de contenu.
 *
 * Le nom de fichier vient normalement de Content-Disposition. Cet en-tête
 * traverse deux domaines et le navigateur ne le rend au code que s'il est
 * explicitement exposé : quand il ne l'était pas, l'export retombait sur une
 * extension « .bin » et Windows ne savait plus ouvrir un fichier pourtant
 * valide. Le type de contenu, lui, est toujours lisible : on s'en sert pour
 * que le repli reste juste même si l'en-tête venait à manquer de nouveau.
 */
export function extensionDuType(reponse: Response): string {
  const type = (reponse.headers.get('content-type') || '').split(';')[0].trim();
  return EXTENSIONS[type] || 'bin';
}

export const assistantsService = {
  /** Activités accessibles à l'utilisateur : atelier, campagne, subvention. */
  listerProjets() {
    return requete<Projet[]>('/documents/projets/');
  },

  creerProjet(name: string, description = '') {
    return requete<Projet>('/documents/projets/', {
      method: 'POST',
      body: JSON.stringify({ name, description }),
    });
  },

  chargerProjet(id: number) {
    return requete<ProjetDetail>(`/documents/projets/${id}/`);
  },

  /** Membres de l'activité et équipe qui la porte. */
  listerMembres(id: number) {
    return requete<Membres>(`/documents/projets/${id}/membres/`);
  },

  /** Collègues et équipes que l'utilisateur peut rattacher à cette activité. */
  listerCandidats(id: number) {
    return requete<{ utilisateurs: Personne[]; equipes: EquipeBreve[] }>(
      `/documents/projets/${id}/candidats/`,
    );
  },

  ajouterMembre(id: number, utilisateur: number) {
    return requete<{ detail: string }>(`/documents/projets/${id}/membres/`, {
      method: 'POST',
      body: JSON.stringify({ user: utilisateur }),
    });
  },

  retirerMembre(id: number, utilisateur: number) {
    return appelAuthentifie(buildApiUrl(`/documents/projets/${id}/membres/`), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user: utilisateur }),
    }).then(async (r) => {
      if (!r.ok) {
        const corps = await r.json().catch(() => ({}));
        throw new Error(corps.detail || 'Retrait impossible.');
      }
    });
  },

  /** Rattache une équipe entière à l'activité, ou l'en détache avec null. */
  rattacherEquipe(id: number, equipe: number | null) {
    return requete<{ detail: string }>(`/documents/projets/${id}/membres/`, {
      method: 'POST',
      body: JSON.stringify({ team: equipe ?? '' }),
    });
  },

  listerDocumentsProjet(id: number) {
    return requete<DocumentActivite[]>(`/documents/projets/${id}/documents/`);
  },

  /**
   * Téléverse un document d'activité.
   *
   * Passe par FormData, donc sans en-tête JSON : le navigateur doit poser
   * lui-même la frontière multipart.
   */
  async televerserDocumentProjet(id: number, fichier: File) {
    const corps = new FormData();
    corps.append('file', fichier);

    const reponse = await appelAuthentifie(buildApiUrl(`/documents/projets/${id}/documents/`), {
      method: 'POST',
      body: corps,
    });
    if (!reponse.ok) {
      const erreur = await reponse.json().catch(() => ({}));
      throw new Error(erreur.detail || "Le fichier n'a pas pu être ajouté.");
    }
    return (await reponse.json()) as DocumentActivite;
  },

  supprimerDocumentProjet(id: number, documentId: number) {
    return appelAuthentifie(buildApiUrl(`/documents/projets/${id}/documents/${documentId}/`), {
      method: 'DELETE',
    }).then((r) => {
      if (!r.ok) throw new Error('Suppression impossible.');
    });
  },

  /** Réunions de l'utilisateur, ou celles d'une activité. */
  listerReunions(projet?: number) {
    return requete<Reunion[]>(
      `/documents/reunions/${projet ? `?projet=${projet}` : ''}`,
    );
  },

  /** Réunion avec sa transcription et ses tours de parole, pour la relecture. */
  chargerReunion(id: number) {
    return requete<Reunion>(`/documents/reunions/${id}/`, {}, 60000);
  },

  /**
   * Dépose un enregistrement et rend la réunion créée avec le numéro du travail.
   *
   * Le dépôt ne rend pas le compte rendu : une réunion d'une heure demande
   * plusieurs minutes à transcrire, et c'est exactement le genre de requête que
   * le proxy coupe. On suit ensuite l'avancement du travail.
   */
  async deposerReunion(
    fichier: File | Blob,
    /**
     * Activité : un numéro existant, un nom à créer, ou rien du tout. En
     * séance, on enregistre d'abord et on range ensuite.
     */
    projet: number | string | null,
    champs: {
      titre?: string;
      tenue_le?: string;
      moteur?: string;
      plan?: string;
      planFichier?: File | null;
    } = {},
    onProgres?: (part: number) => void,
  ) {
    const corps = new FormData();
    corps.append('enregistrement', fichier, (fichier as File).name || 'reunion.wav');
    if (projet) corps.append('projet', String(projet));
    if (champs.titre) corps.append('titre', champs.titre);
    if (champs.tenue_le) corps.append('tenue_le', champs.tenue_le);
    if (champs.moteur) corps.append('moteur', champs.moteur);
    if (champs.plan) corps.append('plan', champs.plan);
    if (champs.planFichier) corps.append('plan_fichier', champs.planFichier, champs.planFichier.name);

    // L'envoi est suivi octet par octet : depuis Conakry, un enregistrement de
    // cent mégaoctets prend plusieurs minutes et une barre figée fait croire à
    // un blocage. fetch() ne sait pas rendre compte de la progression d'un
    // envoi, d'où le recours à XMLHttpRequest ici seulement.
    const envoyer = () => new Promise<{ statut: number; corps: any }>((resoudre, rejeter) => {
      const requeteXhr = new XMLHttpRequest();
      requeteXhr.open('POST', buildApiUrl('/documents/reunions/'));
      requeteXhr.setRequestHeader('Authorization', `Bearer ${authManager.getAccessToken()}`);

      requeteXhr.upload.onprogress = (evenement) => {
        if (onProgres && evenement.lengthComputable) {
          onProgres(evenement.loaded / evenement.total);
        }
      };
      requeteXhr.onload = () => {
        let corpsReponse: any = {};
        try { corpsReponse = JSON.parse(requeteXhr.responseText); } catch { /* corps vide */ }
        resoudre({ statut: requeteXhr.status, corps: corpsReponse });
      };
      requeteXhr.onerror = () => rejeter(
        new Error("L'envoi a été interrompu. Vérifiez votre connexion et réessayez."),
      );
      requeteXhr.send(corps);
    });

    // Un envoi long traverse l'expiration du jeton : on renouvelle et on rejoue
    // plutôt que de faire recommencer un téléversement de plusieurs minutes.
    let reponse = await envoyer();
    if (reponse.statut === 401 && (await authManager.renouvelerJeton())) {
      onProgres?.(0);
      reponse = await envoyer();
    }
    if (reponse.statut < 200 || reponse.statut >= 300) {
      throw new Error(reponse.corps?.detail || "L'enregistrement n'a pas pu être déposé.");
    }
    return reponse.corps as Reunion & { tache: number };
  },

  /**
   * Enregistre les corrections apportées au compte rendu avant validation.
   *
   * C'est la version relue qui sera versée, pas celle du modèle : sans cette
   * étape, la relecture n'aurait aucun effet.
   */
  corrigerReunion(id: number, compteRendu: CompteRendu, projet?: number | string) {
    return requete<Reunion>(`/documents/reunions/${id}/corriger/`, {
      method: 'PATCH',
      body: JSON.stringify(
        projet ? { compte_rendu: compteRendu, projet } : { compte_rendu: compteRendu },
      ),
    });
  },

  /**
   * Supprime une réunion et tout ce qu'elle a laissé.
   *
   * Emporte l'enregistrement, la transcription et le compte rendu déjà versé :
   * le laisser en place le maintiendrait citable par l'assistant documentaire.
   */
  supprimerReunion(id: number) {
    return appelAuthentifie(buildApiUrl(`/documents/reunions/${id}/`), {
      method: 'DELETE',
    }).then(async (r) => {
      if (r.ok) return;
      const corps = await r.json().catch(() => ({}));
      throw new Error(corps.detail || "La réunion n'a pas pu être supprimée.");
    });
  },

  /** Range une réunion dans une activité : numéro existant ou nom à créer. */
  rattacherReunion(id: number, projet: number | string) {
    return requete<Reunion>(`/documents/reunions/${id}/corriger/`, {
      method: 'PATCH',
      body: JSON.stringify({ projet }),
    });
  },

  /**
   * Verse le compte rendu relu à la mémoire documentaire de l'activité.
   *
   * L'activité peut être désignée ici, pour que la choisir et verser tiennent
   * en un geste sur une réunion déposée sans elle.
   */
  validerReunion(id: number, projet?: number | string) {
    return requete<Reunion>(`/documents/reunions/${id}/valider/`, {
      method: 'POST',
      body: JSON.stringify(projet ? { projet } : {}),
    });
  },

  /** Renomme un échange. L'intitulé sert au tri dans « Mes conversations ». */
  renommerConversation(conversationId: number, titre: string) {
    return requete<{ id: number; title: string }>(
      `/documents/conversations/${conversationId}/`,
      { method: 'PATCH', body: JSON.stringify({ title: titre }) },
    );
  },

  /** Supprime un échange et tout ce qu'il contient. Les traces subsistent. */
  supprimerConversation(conversationId: number) {
    return appelAuthentifie(buildApiUrl(`/documents/conversations/${conversationId}/`), {
      method: 'DELETE',
    }).then(async (r) => {
      if (!r.ok) {
        const corps = await r.json().catch(() => ({}));
        throw new Error(corps.detail || "L'échange n'a pas pu être supprimé.");
      }
    });
  },

  /** Rattache une conversation à une activité, ou l'en détache avec null. */
  rattacherConversation(conversationId: number, projet: number | null) {
    return requete<{ id: number; project: number | null; project_name: string | null }>(
      `/documents/conversations/${conversationId}/`,
      { method: 'PATCH', body: JSON.stringify({ project: projet }) },
    );
  },

  /** Conversations de l'utilisateur, de la plus récente à la plus ancienne. */
  listerConversations() {
    return requete<ResumeConversation[]>('/documents/conversations/');
  },

  /**
   * Active ou suspend un compte, et rend son état réel après coup.
   *
   * L'écran d'administration se contentait auparavant de changer l'étiquette
   * affichée : un responsable croyait avoir fermé un accès qui restait ouvert.
   */
  basculerCompte(utilisateur: number | string) {
    return requete<{ is_active: boolean }>(
      `/users/${utilisateur}/toggle_active/`, { method: 'POST' },
    );
  },

  /**
   * Met à jour la fiche d'un compte.
   *
   * Le rôle et l'activation ne sont acceptés que d'un administrateur : le
   * serveur le vérifie, l'écran ne fait que proposer.
   */
  modifierCompte(utilisateur: number | string, champs: {
    first_name?: string; last_name?: string; role?: string;
    department?: number | null; position?: string; phone?: string;
  }) {
    return requete<Record<string, unknown>>(`/users/${utilisateur}/`, {
      method: 'PATCH',
      body: JSON.stringify(champs),
    });
  },

  /** Supprime définitivement un compte. */
  supprimerCompte(utilisateur: number | string) {
    return appelAuthentifie(buildApiUrl(`/users/${utilisateur}/`), {
      method: 'DELETE',
    }).then(async (r) => {
      if (r.ok) return;
      const corps = await r.json().catch(() => ({}));
      throw new Error(corps.detail || corps.error || "Le compte n'a pas pu être supprimé.");
    });
  },

  /**
   * Réinitialise un mot de passe et rend le mot de passe provisoire.
   *
   * Le serveur le renvoie parce qu'il ne peut pas toujours l'envoyer par
   * courriel : tant que la messagerie n'est pas configurée, le transmettre de
   * vive voix est la seule façon d'ouvrir l'accès à quelqu'un.
   */
  reinitialiserMotDePasse(utilisateur: number | string) {
    return requete<{ temporary_password: string; courriel_envoye: boolean }>(
      `/users/${utilisateur}/reset_password/`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  },

  /**
   * Change son propre mot de passe, après vérification de l'actuel.
   *
   * L'écran simulait l'enregistrement : il attendait une seconde et demie puis
   * annonçait un succès, sans jamais appeler le serveur.
   */
  changerMotDePasse(ancien: string, nouveau: string) {
    return requete<{ detail: string }>('/users/mot-de-passe/', {
      method: 'POST',
      body: JSON.stringify({ ancien, nouveau }),
    });
  },

  /** Journalisation et consommation. Refusé aux comptes non administrateurs. */
  chargerJournal() {
    return requete<Journal>('/documents/assistants/journal/', {}, 30000);
  },

  /** Annuaire des numéros et assistants ouverts au canal. Administrateurs seuls. */
  chargerCanalWhatsApp() {
    return requete<CanalWhatsApp>('/documents/assistants/whatsapp/', {}, 30000);
  },

  /** Rattache un numéro à un compte ; renvoie le code de confirmation. */
  rattacherNumero(utilisateur: number, numero: string) {
    return requete<{ id: number; numero: string; code: string; envoye: boolean }>(
      '/documents/assistants/whatsapp/',
      { method: 'POST', body: JSON.stringify({ user: utilisateur, numero }) },
    );
  },

  /**
   * Refait et renvoie le code de confirmation.
   *
   * Un code perdu ne doit pas obliger à retirer le numéro puis à le rattacher
   * de nouveau. Quand la passerelle est appairée, le code part par WhatsApp.
   */
  renvoyerCode(id: number) {
    return requete<{ id: number; code: string; envoye: boolean }>(
      '/documents/assistants/whatsapp/',
      { method: 'POST', body: JSON.stringify({ id, renvoyer: true }) },
    );
  },

  /** Confirme un numéro avec le code reçu par son titulaire. */
  confirmerNumero(id: number, code: string) {
    return requete<{ detail: string }>('/documents/assistants/whatsapp/', {
      method: 'POST',
      body: JSON.stringify({ id, code }),
    });
  },

  /** Ouvre ou ferme un assistant au canal WhatsApp. */
  ouvrirAuCanal(template: number, ouvert: boolean) {
    return requete<{ id: number; ouvert: boolean }>('/documents/assistants/whatsapp/', {
      method: 'PATCH',
      body: JSON.stringify({ template, ouvert }),
    });
  },

  retirerNumero(id: number) {
    return appelAuthentifie(buildApiUrl('/documents/assistants/whatsapp/'), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id }),
    }).then((r) => {
      if (!r.ok) throw new Error('Retrait impossible.');
    });
  },

  /**
   * Image du code à scanner pour appairer un téléphone.
   *
   * Le téléphone qui scanne devient l'émetteur : c'est son compte WhatsApp
   * qui portera les échanges. D'où le numéro dédié.
   */
  async qrWhatsApp() {
    const reponse = await appelAuthentifie(buildApiUrl('/documents/assistants/whatsapp/qr/'), {
    });
    if (!reponse.ok) {
      const corps = await reponse.json().catch(() => ({}));
      throw new Error(corps.detail || "Aucun code disponible.");
    }
    return reponse.blob();
  },

  /** Relance la session pour obtenir un nouveau code. */
  relancerWhatsApp() {
    return requete<Record<string, unknown>>('/documents/assistants/whatsapp/qr/', {
      method: 'POST',
    }, 30000);
  },

  /**
   * Produit le visuel décrit par la consigne.
   *
   * La consigne passée en paramètre prime sur celle enregistrée : c'est la
   * version relue et retouchée qui doit être exécutée.
   */
  async genererVisuel(
    conversationId: number,
    messageId: number,
    invite?: string,
    format?: string,
    legende?: string,
    composition?: string,
  ) {
    const parametres = new URLSearchParams();
    if (invite) parametres.set('invite', invite);
    // La légende retouchée prime elle aussi : c'est elle qui sera posée sur
    // l'affiche, pas celle qu'avait rédigée l'assistant.
    if (legende) parametres.set('legende', legende);
    // « format » est réservé par DRF : il déclencherait une négociation
    // de contenu et une réponse 404.
    if (format) parametres.set('proportions', format);
    // Sans ce choix, toutes les affiches sortaient sur la même mise en page.
    if (composition) parametres.set('composition', composition);

    const reponse = await appelAuthentifie(
      buildApiUrl(
        `/documents/conversations/${conversationId}/messages/${messageId}/visuel/`
        + (parametres.toString() ? `?${parametres}` : ''),
      ),
          );
    if (!reponse.ok) {
      const corps = await reponse.json().catch(() => ({}));
      throw new Error(corps.detail || "Le visuel n'a pas pu être produit.");
    }
    return reponse.blob();
  },

  /**
   * Récupère le dernier visuel produit, sans en fabriquer un nouveau.
   *
   * Sert à retrouver son affiche en rouvrant une conversation. Une image
   * coûte : la reproduire pour la réafficher serait payer deux fois.
   */
  async visuelConserve(conversationId: number, messageId: number) {
    const reponse = await appelAuthentifie(
      buildApiUrl(
        `/documents/conversations/${conversationId}/messages/${messageId}/visuel-conserve/`,
      ),
    );
    if (!reponse.ok) {
      const corps = await reponse.json().catch(() => ({}));
      throw new Error(corps.detail || "Aucun visuel conservé.");
    }
    return reponse.blob();
  },

  /**
   * Met le montage en file et rend le numéro de la tâche.
   *
   * Le montage se faisait dans la requête : une capsule demandait quatre
   * minutes et se terminait en 502, le proxy coupant une requête restée
   * muette. On dépose le travail, puis on suit son avancement.
   */
  demanderVideo(conversationId: number, messageId: number, voix?: string) {
    const parametres = voix ? `?voix=${encodeURIComponent(voix)}` : '';
    return requete<Tache>(
      `/documents/conversations/${conversationId}/messages/${messageId}/video/${parametres}`,
      {},
      60000,
    );
  },

  /**
   * Montage déjà en cours sur ce message, s'il y en a un.
   *
   * Ne déclenche rien : quitter la page pendant un montage ne doit ni le
   * perdre de vue, ni en lancer un second qui serait facturé une deuxième fois.
   */
  async montageEnCours(conversationId: number, messageId: number) {
    const reponse = await appelAuthentifie(buildApiUrl(
      `/documents/conversations/${conversationId}/messages/${messageId}/video/?suivi=1`,
    ));
    if (reponse.status === 204 || !reponse.ok) return null;
    return (await reponse.json()) as Tache;
  },

  /** État d'un travail en cours. */
  suivreTache(tacheId: number) {
    return requete<Tache>(`/documents/conversations/taches/${tacheId}/`, {}, 30000);
  },

  /** Production d'un travail terminé. */
  async recupererProduction(tacheId: number) {
    const reponse = await appelAuthentifie(
      buildApiUrl(`/documents/conversations/taches/${tacheId}/?fichier=1`),
    );
    if (!reponse.ok) {
      const corps = await reponse.json().catch(() => ({}));
      throw new Error(corps.detail || "La production n'a pas pu être récupérée.");
    }
    return reponse.blob();
  },

  /** Voix disponibles sur le compte de synthèse. */
  listerVoix() {
    return requete<{ voix: Voix[] }>('/documents/assistants/voix/', {}, 30000);
  },

  /**
   * Fait dire un texte par la voix choisie.
   *
   * Accepte n'importe quel texte, pas seulement une production enregistrée :
   * c'est ce qui permet de retoucher un spot et de le réécouter jusqu'à ce
   * qu'il sonne juste, avant de le porter à l'antenne.
   */
  async synthetiser(texte: string, voix?: string, titre?: string) {
    const reponse = await appelAuthentifie(buildApiUrl('/documents/assistants/voix/'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ texte, voix, titre }),
    });
    if (!reponse.ok) {
      const corps = await reponse.json().catch(() => ({}));
      throw new Error(corps.detail || "La voix n'a pas pu être produite.");
    }
    return reponse.blob();
  },

  /**
   * Monte toutes les répliques d'un contenu sonore en un seul fichier.
   *
   * On transmet les textes retouchés et la voix de chaque locuteur : c'est la
   * version entendue à l'écran qui doit être livrée, pas celle d'origine.
   */
  async monterSpot(
    conversationId: number,
    messageId: number,
    reglages: { voix: Record<string, string>; textes: Record<string, string> },
  ) {
    const reponse = await appelAuthentifie(
      buildApiUrl(
        `/documents/conversations/${conversationId}/messages/${messageId}/spot/`,
      ),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reglages),
      },
    );
    if (!reponse.ok) {
      const corps = await reponse.json().catch(() => ({}));
      throw new Error(corps.detail || "Le spot n'a pas pu être monté.");
    }
    return reponse.blob();
  },

  /** Oriente une demande en langage naturel vers les assistants adaptés. */
  orienter(query: string) {
    return requete<{ suggestions: SuggestionAssistant[]; commentaire: string }>(
      '/documents/assistants/orienter/',
      { method: 'POST', body: JSON.stringify({ query }) },
      60000,
    );
  },

  /**
   * Ouvre un échange sur un assistant.
   *
   * Le rattachement à une activité se fait dès l'ouverture quand on part d'un
   * projet : l'échange y figure alors sans manipulation supplémentaire, et il
   * hérite immédiatement des documents du dossier.
   */
  ouvrirConversation(templateId: number, projet?: number) {
    return requete<{ id: number; template: number; title: string }>(
      '/documents/conversations/',
      {
        method: 'POST',
        body: JSON.stringify({ template: templateId, ...(projet ? { project: projet } : {}) }),
      },
    );
  },

  /**
   * Ouvre une conversation à partir du premier message.
   *
   * Sans agent désigné, le serveur le choisit d'après le message ; le message
   * lui-même est ensuite envoyé par l'écran de conversation, en flux.
   */
  demarrerConversation(message: string, template?: number | null, projet?: number | null) {
    return requete<{
      id: number;
      template: number;
      template_title: string;
      automatique: boolean;
      raison: string;
    }>(
      '/documents/conversations/demarrer/',
      {
        method: 'POST',
        body: JSON.stringify({
          message,
          ...(template ? { template } : {}),
          ...(projet ? { project: projet } : {}),
        }),
      },
      60000,
    );
  },

  chargerConversation(conversationId: number) {
    return requete<ConversationDetail>(`/documents/conversations/${conversationId}/`);
  },

  envoyerMessage(conversationId: number, contenu: string) {
    return requete<AssistantMessage>(
      `/documents/conversations/${conversationId}/messages/`,
      { method: 'POST', body: JSON.stringify({ content: contenu }) },
      TIMEOUT_GENERATION,
    );
  },

  /**
   * Envoie un message et suit la génération au fur et à mesure.
   *
   * On lit le flux avec fetch plutôt qu'avec EventSource : celui-ci ne sait
   * pas porter l'en-tête d'autorisation, et le jeton n'a rien à faire dans une
   * URL. `surEvenement` est appelé à chaque étape ; la promesse se résout sur
   * le message enregistré.
   */
  async envoyerMessageEnFlux(
    conversationId: number,
    contenu: string,
    surEvenement: (evenement: EvenementFlux) => void,
    /** Passe outre les questions de cadrage et rédige immédiatement. */
    sansQuestions = false,
  ): Promise<AssistantMessage> {
    const reponse = await appelAuthentifie(
      buildApiUrl(`/documents/conversations/${conversationId}/messages/flux/`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: contenu, sans_questions: sansQuestions }),
      },
    );

    if (!reponse.ok || !reponse.body) {
      const corps = await reponse.json().catch(() => ({}));
      throw new Error(corps.detail || `Erreur ${reponse.status}`);
    }

    const lecteur = reponse.body.getReader();
    const decodeur = new TextDecoder();
    let tampon = '';
    let resultat: AssistantMessage | null = null;

    // Un événement peut arriver coupé en deux : on ne traite que les blocs
    // complets (séparés par une ligne vide) et on garde le reste en tampon.
    while (true) {
      const { done, value } = await lecteur.read();
      if (done) break;

      tampon += decodeur.decode(value, { stream: true });
      const blocs = tampon.split('\n\n');
      tampon = blocs.pop() ?? '';

      for (const bloc of blocs) {
        const ligne = bloc.split('\n').find((l) => l.startsWith('data: '));
        if (!ligne) continue;

        const evenement = JSON.parse(ligne.slice(6)) as EvenementFlux;
        if (evenement.type === 'erreur') throw new Error(evenement.detail);
        if (evenement.type === 'fin') resultat = evenement.message;
        surEvenement(evenement);
      }
    }

    if (!resultat) throw new Error('La génération a été interrompue.');
    return resultat;
  },

  /** Enregistre la grille corrigée avant export. */
  enregistrerCorrections(conversationId: number, messageId: number, payload: any) {
    return requete<AssistantMessage>(
      `/documents/conversations/${conversationId}/messages/${messageId}/`,
      { method: 'PATCH', body: JSON.stringify({ payload }) },
    );
  },

  /**
   * Récupère la narration audio d'une diapositive.
   *
   * Une piste par diapositive : on peut réécouter ou refaire une diapositive
   * seule, sans regénérer tout l'audio de la présentation.
   */
  async narrationSlide(conversationId: number, messageId: number, index: number) {
    const reponse = await appelAuthentifie(
      buildApiUrl(
        `/documents/conversations/${conversationId}/messages/${messageId}/voix/${index}/`,
      ),
          );
    if (!reponse.ok) {
      const corps = await reponse.json().catch(() => ({}));
      throw new Error(corps.detail || "La narration n'a pas pu être générée.");
    }
    return reponse.blob();
  },

  /** Récupère le fichier généré sans le télécharger, pour l'afficher. */
  async telechargerBlob(conversationId: number, messageId: number) {
    const reponse = await appelAuthentifie(
      buildApiUrl(`/documents/conversations/${conversationId}/messages/${messageId}/export/`),
          );
    if (!reponse.ok) {
      const corps = await reponse.json().catch(() => ({}));
      throw new Error(corps.detail || "Le document n'a pas pu être généré.");
    }
    return reponse.blob();
  },

  /**
   * Déclenche le téléchargement du fichier généré.
   *
   * L'extension vient du serveur : c'est lui qui décide du format selon le
   * cas d'usage — tableur pour un budget, Word pour un document.
   */
  async exporter(
    conversationId: number,
    messageId: number,
    nomFichier: string,
    /** Mise en forme choisie pour les présentations : charte maison ou Gamma. */
    moteur?: 'gamma',
  ) {
    const reponse = await appelAuthentifie(
      buildApiUrl(
        `/documents/conversations/${conversationId}/messages/${messageId}/export/`
        + (moteur ? `?moteur=${moteur}` : ''),
      ),
          );

    if (!reponse.ok) {
      const corps = await reponse.json().catch(() => ({}));
      throw new Error(corps.detail || "L'export a échoué.");
    }

    // Le nom et l'extension réels sont dans l'en-tête renvoyé par le serveur.
    // On privilégie la forme UTF-8 (RFC 6266), qui conserve les accents.
    const entete = reponse.headers.get('content-disposition') || '';
    const utf8 = entete.match(/filename\*=UTF-8''([^;]+)/i);
    const ascii = entete.match(/filename="?([^";]+)"?/);
    const nomServeur = utf8 ? decodeURIComponent(utf8[1]) : ascii?.[1];

    const blob = await reponse.blob();
    const url = window.URL.createObjectURL(blob);
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = nomServeur || `${nomFichier}.${extensionDuType(reponse)}`;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    window.URL.revokeObjectURL(url);
  },
};
